import React, { useEffect, useRef, useState } from 'react';
import { IconSpinner, SecondaryButton } from '../../../components';
import { IconCameraL } from '../../../icons';
import css from './TakeCameraPhoto.css';

const TO_DATA_URL = 'toDataURL';
const TO_BLOB = 'toBlob';

const convertConfig = {
    [TO_DATA_URL]: {
        convert: (canvas, type = 'image/png') => new Promise(res => res(canvas.toDataURL(type))),
    },
    [TO_BLOB]: {
        convert: canvas => new Promise(res => canvas.toBlob(async blob => res(blob))),
    },
};

export default ({ onImageUpload, convertTo = TO_DATA_URL }) => {
    const [streaming, setStreaming] = useState(false);
    /** height & width will be computed based on the input stream  */
    const [height, setHeight] = useState(320);
    const [width, setWidth] = useState(320);

    const videoRef = useRef();
    const canvasRef = useRef();
    const streamsRef = useRef();

    const interruptStreaming = () =>
        streamsRef?.current && streamsRef.current.getTracks().forEach(track => track?.stop());

    const clearPhoto = () => {
        const context = canvasRef.current.getContext('2d');
        context.fillStyle = '#AAA';
        context.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    };

    const takePicture = async e => {
        e.preventDefault();

        const context = canvasRef.current.getContext('2d');

        if (width && height) {
            canvasRef.current.width = width;
            canvasRef.current.height = height;

            context.drawImage(videoRef.current, 0, 0, width, height);
            const { convert } = convertConfig[convertTo];
            const data = await convert(canvasRef.current);

            interruptStreaming();
            onImageUpload(data);
        } else {
            clearPhoto();
        }
    };

    const initStreaming = () => {
        if (!streaming) {
            const height = videoRef.current.videoHeight;
            const width = videoRef.current.videoWidth;

            setHeight(height);
            setWidth(width);
            /**
             * Firefox currently has a bug where the height can't be read from
             * the video, so we will make assumptions if this happens.
             */
            if (isNaN(height)) {
                height = width / (4 / 3);
            }

            canvasRef.current.setAttribute('width', width);
            canvasRef.current.setAttribute('height', height);

            setStreaming(true);
        }
    };

    function startup() {
        window.navigator.mediaDevices
            .getUserMedia({ video: true, audio: false })
            .then(stream => {
                videoRef.current.srcObject = stream;
                videoRef.current.play();
                streamsRef.current = stream;
            })
            .catch(err => {
                console.error(`An error occurred: ${err}`);
            });
    }

    useEffect(() => {
        startup();

        videoRef.current.addEventListener('canplay', initStreaming, false);

        return () => {
            interruptStreaming();
            videoRef.current &&
                videoRef.current.removeEventListener('canplay', initStreaming, false);
        };
    }, []);

    return (
        <div className={css.root}>
            <div className={css.camera}>
                <video ref={videoRef}>Video stream not available.</video>
            </div>
            {!streaming && <IconSpinner />}
            {streaming && (
                <SecondaryButton onClick={takePicture}>
                    <IconCameraL />
                    <span>Foto machen</span>
                </SecondaryButton>
            )}
            <canvas ref={canvasRef} />
        </div>
    );
};
